import MaterialIcon from '@components/common/MaterialIcon';
import ButtonIconT from '@components/tailwind-components/Button/ButtonIconT';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import CheckboxInputT from '@components/tailwind-components/Input/CheckboxInputT';
import CronInput from '@components/tailwind-components/Input/CronInput';
import InputT from '@components/tailwind-components/Input/InputT';
import InputWithLabelT from '@components/tailwind-components/Input/InputWithLabelT';
import SelectInputWithLabelT from '@components/tailwind-components/Input/SelectInputWithLabelT';
import { ExportDataType } from '@conversed/shared/src/central/dto/export.dto';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  type Control,
  type UseFormWatch,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toastErrorTranslated } from '../../../../helpers/toast';
import { useModal } from '../../../../hooks/modal/useModal';
import type { TExportConfig } from '../../../../interfaces/redux/TSettingsState';
import { ScheduleInput } from '../../../tailwind-components/Input/ScheduleInput';
import { TimeInput } from '../../../tailwind-components/Input/TimeInput';

type TExportConfigFormData = Omit<
  TExportConfig,
  'requiredFields' | 'notifyViaEmail'
> & {
  requiredFields: { 0: string; 1: unknown }[];
  recipients: { email: string }[];
};

type TExportConfigDataModal = {
  onSubmit: (exportConfig: TExportConfig) => void;
  data: TExportConfig;
  hasHistoryExport?: boolean;
};

type TScheduleType = 'interval' | 'schedule' | 'time';

type TExportConfigTimeInput = {
  control: Control;
  watch: UseFormWatch<TExportConfig>;
  scheduleType: TScheduleType;
};

const SectionHeading: React.FC<{ text: string; className?: string }> = ({
  text,
  className = '',
}) => (
  <h5 className={`text-sm font-semibold text-primary-base mb-1 ${className}`}>
    {text}
  </h5>
);

const SectionLockedNotice: React.FC<{ text: string }> = ({ text }) => (
  <div className='mt-2 flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded'>
    <MaterialIcon icon='lock' classes={['text-yellow-600', 'text-sm']} />
    <span className='text-tiny italic'>{text}</span>
  </div>
);

const ExportConfigTimeInput: React.FC<TExportConfigTimeInput> = ({
  scheduleType,
  control,
  watch,
}) => {
  const { t } = useTranslation();

  if (scheduleType === 'schedule') {
    return (
      <ScheduleInput
        control={control}
        name={'exportTime'}
        watch={watch}
        label={t('settings.export-config.export-time')}
      />
    );
  }
  if (scheduleType === 'time') {
    return (
      <TimeInput
        control={control}
        name={'exportTime'}
        watch={watch}
        label={t('settings.export-config.export-time')}
      />
    );
  }

  return (
    <CronInput
      control={control}
      name={'exportTime'}
      watch={watch}
      label={t('settings.export-config.cron-time')}
    />
  );
};

export const ExportConfigDataModal: React.FC<TExportConfigDataModal> = ({
  onSubmit,
  data,
  hasHistoryExport = false,
}) => {
  const [scheduleType, setScheduleType] = useState<TScheduleType>(() => {
    const time = data.exportTime;
    if (time.includes(':')) {
      return 'time';
    }

    if (time.split(' ').length === 5) {
      return 'interval';
    }

    return 'schedule';
  });

  const { hideModal } = useModal();
  const { t } = useTranslation();

  const { control, watch, handleSubmit, setValue } = useForm({
    shouldUseNativeValidation: true,
    defaultValues: {
      ...data,
      // * Ensure selected data-type is always distinct if it doesn't exist
      dataType: !data.dataType ? ExportDataType.Distinct : data.dataType,
      requiredFields: Object.entries(data?.requiredFields || {}).map(
        ([key, value]) => {
          return {
            '0': key,
            '1': value,
          };
        },
      ),
      recipients: (data.notifyViaEmail?.recipients ?? []).map((email) => ({
        email,
      })),
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'requiredFields',
  });

  const {
    fields: recipientFields,
    append: appendRecipient,
    remove: removeRecipient,
  } = useFieldArray({
    control,
    name: 'recipients',
  });

  const watchedDataType = watch('dataType');
  const isHistoryExport = watchedDataType === ExportDataType.History;
  const isWeeklyHotTopicsExport =
    watchedDataType === ExportDataType.WeeklyHotTopics;

  // * Keep the stored exportTime in sync with the fixed 04:00 cron so editing a history config shows the correct time.
  useEffect(() => {
    if (isHistoryExport) {
      setValue('exportTime', '04:00');
      setScheduleType('time');
    }
  }, [isHistoryExport, setValue]);

  // * For weekly-hot-topics: directory is always fixed; default schedule to Monday 06:00 UTC.
  useEffect(() => {
    if (isWeeklyHotTopicsExport) {
      setValue('directory', 'weekly-hot-topic-export');
      if (!watch('exportTime') || watch('exportTime') === '08:00') {
        setValue('exportTime', '0 6 * * 1');
        setScheduleType('interval');
      }
    } else if (watch('directory') === 'weekly-hot-topic-export') {
      setValue('directory', '');
    }
  }, [isWeeklyHotTopicsExport, setValue, watch]);

  const convertData = useCallback(
    (data: TExportConfigFormData): TExportConfig => {
      const { recipients, ...rest } = data;
      return {
        ...rest,
        requiredFields: Object.assign(
          {},
          ...data.requiredFields.map((exportFields) => ({
            [exportFields[0]]: exportFields[1],
          })),
        ) as TExportConfig['requiredFields'],
        notifyViaEmail:
          data.dataType === ExportDataType.WeeklyHotTopics &&
          recipients.length > 0
            ? {
                recipients: recipients
                  .map((recipient) => recipient.email)
                  .filter(Boolean),
                subject: 'Weekly Question & Hot Topic Export',
              }
            : undefined,
      };
    },
    [],
  );

  const hideAndSubmit = (data: TExportConfigFormData) => {
    const convertedData = convertData(data);

    // * Guard against submitting a second history config in case the disabled dropdown option was bypassed.
    if (convertedData.dataType === ExportDataType.History && hasHistoryExport) {
      toastErrorTranslated(
        'settings.export-config.error.history-already-exists',
      );
      return;
    }

    onSubmit({
      ...convertedData,
    });
    hideModal();
  };

  return (
    <form
      className='tailwind-modal-form'
      onSubmit={handleSubmit(hideAndSubmit)}>
      <div className='tailwind-modal-body'>
        <SectionHeading text={t('settings.export-config.section.general')} />

        <InputWithLabelT
          type='text'
          field='name'
          placeholder={t('settings.export-config.name')}
          label={t('settings.export-config.name')}
          id='name'
          control={control}
          options={{
            required: t('settings.export-config.validation.name'),
          }}
        />
        <InputWithLabelT
          type='text'
          field='directory'
          placeholder={t('settings.export-config.directory')}
          label={t('settings.export-config.directory')}
          id='directory'
          control={control}
          options={{ disabled: isWeeklyHotTopicsExport }}
        />

        <SelectInputWithLabelT
          field='dataType'
          label={t('settings.export-config.data-type')}
          id='export-data-type'
          control={control}
          values={[
            {
              value: ExportDataType.Distinct,
              description: t('settings.export-config.data-type.distinct'),
            },
            {
              value: ExportDataType.Count,
              description: t('settings.export-config.data-type.count'),
            },
            {
              value: ExportDataType.History,
              description: t('settings.export-config.data-type.history'),
              // * Disable the history option when one already exists; kept enabled when editing the existing history config.
              optionProps:
                hasHistoryExport && !isHistoryExport
                  ? { disabled: true }
                  : undefined,
            },
            {
              value: ExportDataType.Total,
              description: t('settings.export-config.data-type.total'),
            },
            {
              value: ExportDataType.WeeklyHotTopics,
              description: t(
                'settings.export-config.data-type.weekly-hot-topics',
              ),
            },
          ]}
        />

        <SectionHeading
          className='mt-4'
          text={t('settings.export-config.required-fields')}
        />

        {isHistoryExport || isWeeklyHotTopicsExport ? (
          <SectionLockedNotice
            text={t(
              isWeeklyHotTopicsExport
                ? 'settings.export-config.weekly-hot-topics-fields-locked'
                : 'settings.export-config.history-fields-locked',
            )}
          />
        ) : (
          <>
            {fields.map((_, index) => {
              return (
                <div className='mt-1 flex items-center gap-x-4' key={index}>
                  <InputT
                    parentClassName='flex-1 max-w-[170px]'
                    type='text'
                    field={`requiredFields.${index}.1`}
                    placeholder={t(
                      'settings.export-config.required-fields.value',
                    )}
                    control={control}
                    options={{
                      required: t(
                        'settings.export-config.validation.required-fields.value',
                      ),
                    }}
                  />
                  <div className='mb-2'>{String('-')}</div>
                  <InputT
                    parentClassName='flex-1 max-w-[170px]'
                    type='text'
                    field={`requiredFields.${index}.0`}
                    placeholder={t(
                      'settings.export-config.required-fields.key',
                    )}
                    control={control}
                    options={{
                      required: t(
                        'settings.export-config.validation.required-fields.key',
                      ),
                    }}
                  />
                  <div className='mb-2'>
                    <ButtonIconT
                      icon='delete'
                      onClick={() => remove(index)}
                      color='danger'
                    />
                  </div>
                </div>
              );
            })}

            <ButtonIconT
              className='mb-3'
              icon='add_circle'
              onClick={() => append({ '0': '', '1': '' })}
              label={t('export-config.button-add')}
              color='primary-base'
            />
          </>
        )}

        {isWeeklyHotTopicsExport && (
          <>
            <SectionHeading
              className='mt-4'
              text={t('settings.export-config.recipients')}
            />
            {recipientFields.map((field, index) => (
              <div className='mt-1 flex items-center gap-x-4' key={field.id}>
                <InputT
                  parentClassName='flex-1'
                  type='email'
                  field={`recipients.${index}.email`}
                  placeholder={t('settings.export-config.recipients.email')}
                  control={control}
                  options={{
                    required: t(
                      'settings.export-config.validation.recipients.email',
                    ),
                  }}
                />
                <div className='mb-2'>
                  <ButtonIconT
                    icon='delete'
                    onClick={() => removeRecipient(index)}
                    color='danger'
                  />
                </div>
              </div>
            ))}
            <ButtonIconT
              className='mb-3'
              icon='add_circle'
              onClick={() => appendRecipient({ email: '' })}
              label={t('settings.export-config.recipients.add')}
              color='primary-base'
            />
          </>
        )}

        <SectionHeading
          className='mt-4'
          text={t('settings.export-config.options')}
        />

        <CheckboxInputT
          label={t('settings.export-config.is-non-incremental')}
          control={control}
          field={'isNonIncremental'}
          id='isNonIncremental'
        />
        <CheckboxInputT
          label={t('settings.export-config.skip-empty-export')}
          control={control}
          field={'skipEmptyExport'}
          id='skipEmptyExport'
        />

        <SelectInputWithLabelT
          field='format'
          label={t('settings.export-config.export-format')}
          id='export-format'
          control={control}
          values={[
            { value: 'xlsx', description: 'Excel' },
            { value: 'csv', description: 'CSV' },
          ]}
        />

        <SectionHeading
          className='mt-4'
          text={t('settings.export-config.schedule')}
        />

        {isHistoryExport ? (
          <SectionLockedNotice
            text={t('settings.export-config.history-time-locked')}
          />
        ) : (
          <>
            <div className='flex flex-wrap gap-y-1'>
              {(['interval', 'schedule', 'time'] as const).map(
                (type, index) => {
                  const isSelected = scheduleType === type;

                  return (
                    <button
                      type='button'
                      id={`tbg-btn-${index + 1}`}
                      key={type}
                      className={`px-3 py-2 text-gray-500 text-sm font-normal flex-auto border-r max-w-[105px] ${
                        isSelected ? 'bg-success text-white' : 'bg-gray-200'
                      } ${index < 1 ? 'border-r border-gray-200' : ''}`}
                      onClick={() => setScheduleType(type)}>
                      {t(`settings.export-config.schedule-type.${type}`)}
                    </button>
                  );
                },
              )}
            </div>

            <div className='mt-3 flex items-center'>
              <ExportConfigTimeInput
                control={control as any}
                watch={watch as any}
                scheduleType={scheduleType}
              />
            </div>
            {isWeeklyHotTopicsExport && scheduleType === 'interval' && (
              <button
                type='button'
                className='mt-1 text-xs text-primary-base underline'
                onClick={() => setValue('exportTime', '0 6 * * 1')}>
                {t('settings.export-config.prefill-monday')}
              </button>
            )}
          </>
        )}
      </div>
      <div className='tailwind-modal-footer'>
        <ButtonT
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'
          onClick={hideModal}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT type='submit' styling='primary' size='xs' rounding='md'>
          {t('general.buttons.save')}
        </ButtonT>
      </div>
    </form>
  );
};

export default ExportConfigDataModal;
